import React, { useEffect, useMemo, useRef, useState } from "react";
import BrandsMenu from "./BrandsMenu/BrandsMenu";
import NewProductsMenu from "./NewProductsMenu/NewProductsMenu";
import ShopByHairTypePage from "./ShopByHairType/ShopByHairTypePage";
import { useBrandContext } from "../../../context/BrandContext";
import { Sparkles, X } from "lucide-react";

type MobileView = "main" | "brands" | "newProducts" | "hairType" | "skinType";

const BannerNavRow: React.FC = () => {
  const { brands } = useBrandContext();

  const brandsBtnRef = useRef<HTMLButtonElement>(null);
  const [brandsOpen, setBrandsOpen] = useState(false);

  const newProductsBtnRef = useRef<HTMLButtonElement>(null);
  const [newProductsOpen, setNewProductsOpen] = useState(false);

  const hairTypeBtnRef = useRef<HTMLButtonElement>(null);
  const [hairTypeOpen, setHairTypeOpen] = useState(false);

  const skinTypeBtnRef = useRef<HTMLButtonElement>(null);
  const [skinTypeOpen, setSkinTypeOpen] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>("main");

  const closeTimer = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = null;
  };

  const openBrands = () => {
    clearCloseTimer();
    setBrandsOpen(true);
    setNewProductsOpen(false);
    setHairTypeOpen(false);
    setSkinTypeOpen(false);
  };

  const scheduleCloseBrands = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setBrandsOpen(false), 120);
  };

  const openNewProducts = () => {
    clearCloseTimer();
    setNewProductsOpen(true);
    setBrandsOpen(false);
    setHairTypeOpen(false);
    setSkinTypeOpen(false);
  };

  const scheduleCloseNewProducts = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setNewProductsOpen(false), 120);
  };

  const openHairType = () => {
    clearCloseTimer();
    setHairTypeOpen(true);
    setBrandsOpen(false);
    setNewProductsOpen(false);
    setSkinTypeOpen(false);
  };

  const scheduleCloseHairType = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setHairTypeOpen(false), 120);
  };

  const openSkinType = () => {
    clearCloseTimer();
    setSkinTypeOpen(true);
    setBrandsOpen(false);
    setNewProductsOpen(false);
    setHairTypeOpen(false);
  };

  const scheduleCloseSkinType = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setSkinTypeOpen(false), 120);
  };

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  useEffect(() => {
    if (!mobileOpen) {
      setMobileView("main");
    }
  }, [mobileOpen]);

  const items = useMemo(
    () => [
      { label: "Brands", kind: "brands" as const },
      { label: "New Products", kind: "newProducts" as const },
      { label: "Deals", kind: "link" as const },
      { label: "Shop by Hair Type", kind: "hairType" as const },
      { label: "Shop by Skin Type", kind: "skinType" as const },
    ],
    []
  );

  const textShadowStyle: React.CSSProperties = {
    textShadow: "0 2px 10px rgba(0,0,0,0.35)",
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <div className="hidden md:flex absolute top-10 left-1/2 -translate-x-1/2 z-30 flex-nowrap gap-8 whitespace-nowrap text-white font-semibold tracking-widest uppercase">
        {items.map((item) => {
          if (item.kind === "brands") {
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={openBrands}
                onMouseLeave={scheduleCloseBrands}
              >
                <button
                  ref={brandsBtnRef}
                  type="button"
                  onClick={() => {
                    setBrandsOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setNewProductsOpen(false);
                        setHairTypeOpen(false);
                        setSkinTypeOpen(false);
                      }
                      return next;
                    });
                  }}
                  className="text-lg md:text-xl hover:scale-105 transition shrink-0"
                  style={textShadowStyle}
                  aria-haspopup="menu"
                  aria-expanded={brandsOpen}
                >
                  {item.label}
                </button>

                <div onMouseEnter={openBrands} onMouseLeave={scheduleCloseBrands}>
                  <BrandsMenu
                    isOpen={brandsOpen}
                    brands={brands}
                    anchorRef={brandsBtnRef}
                    onClose={() => setBrandsOpen(false)}
                  />
                </div>
              </div>
            );
          }

          if (item.kind === "newProducts") {
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={openNewProducts}
                onMouseLeave={scheduleCloseNewProducts}
              >
                <button
                  ref={newProductsBtnRef}
                  type="button"
                  onClick={() => {
                    setNewProductsOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setBrandsOpen(false);
                        setHairTypeOpen(false);
                        setSkinTypeOpen(false);
                      }
                      return next;
                    });
                  }}
                  className="text-lg md:text-xl hover:scale-105 transition shrink-0"
                  style={textShadowStyle}
                  aria-haspopup="menu"
                  aria-expanded={newProductsOpen}
                >
                  {item.label}
                </button>

                <div
                  onMouseEnter={openNewProducts}
                  onMouseLeave={scheduleCloseNewProducts}
                >
                  <NewProductsMenu
                    isOpen={newProductsOpen}
                    anchorRef={newProductsBtnRef}
                    onClose={() => setNewProductsOpen(false)}
                  />
                </div>
              </div>
            );
          }

          if (item.kind === "hairType") {
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={openHairType}
                onMouseLeave={scheduleCloseHairType}
              >
                <button
                  ref={hairTypeBtnRef}
                  type="button"
                  onClick={() => {
                    setHairTypeOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setBrandsOpen(false);
                        setNewProductsOpen(false);
                        setSkinTypeOpen(false);
                      }
                      return next;
                    });
                  }}
                  className="text-lg md:text-xl hover:scale-105 transition shrink-0"
                  style={textShadowStyle}
                  aria-haspopup="menu"
                  aria-expanded={hairTypeOpen}
                >
                  {item.label}
                </button>

                <div
                  onMouseEnter={openHairType}
                  onMouseLeave={scheduleCloseHairType}
                >
                  <ShopByHairTypePage
                    isOpen={hairTypeOpen}
                    anchorRef={hairTypeBtnRef}
                    onClose={() => setHairTypeOpen(false)}
                  />
                </div>
              </div>
            );
          }

          if (item.kind === "skinType") {
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={openSkinType}
                onMouseLeave={scheduleCloseSkinType}
              >
                <button
                  ref={skinTypeBtnRef}
                  type="button"
                  onClick={() => {
                    setSkinTypeOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setBrandsOpen(false);
                        setNewProductsOpen(false);
                        setHairTypeOpen(false);
                      }
                      return next;
                    });
                  }}
                  className="text-lg md:text-xl hover:scale-105 transition shrink-0"
                  style={textShadowStyle}
                  aria-haspopup="menu"
                  aria-expanded={skinTypeOpen}
                >
                  {item.label}
                </button>

                <div
                  onMouseEnter={openSkinType}
                  onMouseLeave={scheduleCloseSkinType}
                >
                  <ShopByHairTypePage
                    isOpen={skinTypeOpen}
                    anchorRef={skinTypeBtnRef}
                    onClose={() => setSkinTypeOpen(false)}
                    groupSlug="skin-type"
                    title="Shop by Skin Type"
                    productParam="skinType"
                  />
                </div>
              </div>
            );
          }

          return (
            <button
              key={item.label}
              type="button"
              className="text-lg md:text-xl hover:scale-105 transition shrink-0"
              style={textShadowStyle}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="md:hidden absolute top-8 left-1/2 -translate-x-1/2 z-40">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="
            inline-flex items-center gap-2
            px-4 py-2
            rounded-full
            bg-white/30 backdrop-blur-md
            border border-white/35
            text-white font-semibold uppercase tracking-widest
            shadow-[0_10px_30px_rgba(0,0,0,0.18)]
            active:scale-[0.98]
            transition
          "
          style={textShadowStyle}
          aria-expanded={mobileOpen}
        >
          <Sparkles size={18} />
          Explore
        </button>
      </div>

      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-[9998] bg-black/10"
            onClick={closeMobile}
          />

          <div className="md:hidden fixed top-20 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md">
            <div
              className="
                rounded-2xl
                bg-white/70 backdrop-blur-xl
                border border-white/60
                shadow-[0_18px_55px_rgba(0,0,0,0.18)]
                overflow-hidden
                max-h-[78vh]
              "
            >
              <div className="flex items-center justify-between px-4 py-3">
                <div className="text-[#5c2f2f] font-bold text-sm">
                  {mobileView === "main"
                    ? "Browse"
                    : mobileView === "brands"
                    ? "Brands"
                    : mobileView === "newProducts"
                    ? "New Products"
                    : mobileView === "hairType"
                    ? "Hair Type"
                    : "Skin Type"}
                </div>

                <button
                  type="button"
                  onClick={closeMobile}
                  className="text-[#5c2f2f] p-2 rounded-full hover:bg-black/5 transition"
                  aria-label="Close menu"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="px-3 pb-3 overflow-y-auto max-h-[70vh]">
                {mobileView === "main" ? (
                  <div className="flex flex-col">
                    {items.map((item) => {
                      if (item.kind === "brands") {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => setMobileView("brands")}
                            className="text-left px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition flex items-center justify-between"
                          >
                            <span>{item.label}</span>
                            <span className="text-[#7a4343]">›</span>
                          </button>
                        );
                      }

                      if (item.kind === "newProducts") {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => setMobileView("newProducts")}
                            className="text-left px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition flex items-center justify-between"
                          >
                            <span>{item.label}</span>
                            <span className="text-[#7a4343]">›</span>
                          </button>
                        );
                      }

                      if (item.kind === "hairType") {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => setMobileView("hairType")}
                            className="text-left px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition flex items-center justify-between"
                          >
                            <span className="flex-1 pr-3 text-[15px] leading-5 break-words normal-case tracking-normal">
                              {item.label}
                            </span>
                            <span className="text-[#7a4343] shrink-0">›</span>
                          </button>
                        );
                      }

                      if (item.kind === "skinType") {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => setMobileView("skinType")}
                            className="text-left px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition flex items-center justify-between"
                          >
                            <span className="flex-1 pr-3 text-[15px] leading-5 break-words normal-case tracking-normal">
                              {item.label}
                            </span>
                            <span className="text-[#7a4343] shrink-0">›</span>
                          </button>
                        );
                      }

                      return (
                        <button
                          key={item.label}
                          type="button"
                          onClick={closeMobile}
                          className="text-left px-3 py-3 rounded-xl text-[#5c2f2f] font-semibold hover:bg-[#f7dada]/50 transition"
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                ) : mobileView === "brands" ? (
                  <BrandsMenu
                    isOpen={true}
                    brands={brands}
                    anchorRef={{ current: null }}
                    onClose={closeMobile}
                    variant="mobile"
                    onBack={() => setMobileView("main")}
                  />
                ) : mobileView === "newProducts" ? (
                  <NewProductsMenu
                    isOpen={true}
                    anchorRef={{ current: null }}
                    onClose={closeMobile}
                    variant="mobile"
                    onBack={() => setMobileView("main")}
                  />
                ) : mobileView === "hairType" ? (
                  <ShopByHairTypePage
                    isOpen={true}
                    anchorRef={{ current: null }}
                    onClose={closeMobile}
                    variant="mobile"
                    onBack={() => setMobileView("main")}
                  />
                ) : (
                  <ShopByHairTypePage
                    isOpen={true}
                    anchorRef={{ current: null }}
                    onClose={closeMobile}
                    variant="mobile"
                    onBack={() => setMobileView("main")}
                    groupSlug="skin-type"
                    title="Shop by Skin Type"
                    productParam="skinType"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BannerNavRow;
